import React, { useMemo, useState } from "react";
import {
  ACCOMMODATION_COLORS,
  formatSubcategory,
  ExportMenu,
  downloadCsv,
  downloadPdf,
} from "./shared";
import ThinScroll from "../shared/ThinScroll";
import { SkeletonText } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

const TIER_CONFIG = {
  1: {
    label: "Direct Demand",
    pill: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  },
  2: {
    label: "Overflow",
    pill: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  },
  3: {
    label: "Extended Stay",
    pill: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  },
};

const ACCOMMODATIONS = [
  { key: "rv", label: "RV", field: "rv_affinity" },
  { key: "cabin", label: "Cabin", field: "cabin_affinity" },
  { key: "glamping", label: "Glamping", field: "glamping_affinity" },
  { key: "tent", label: "Tent", field: "tent_affinity" },
];

const affinityCellStyle = (value) => {
  const clamped = Math.max(0, Math.min(1, Number(value || 0)));
  return { backgroundColor: `rgba(16, 185, 129, ${clamped.toFixed(2)})` };
};

const affinityTextClass = (value) => {
  const affinity = Number(value || 0);
  if (affinity >= 0.55) return "text-white font-bold";
  return "text-gray-800 dark:text-gray-200 font-semibold";
};

const formatAffinity = (value) => Number(value || 0).toFixed(2);

// CSV/PDF column spec — the on-screen heatmap cells become plain affinity numbers,
// and the tier badge splits into its number + label. Subcategory + Notes (shown
// inline / under the category on screen) get their own columns so the export is the
// fuller record. Mirrors OpportunityBoardPanel's EXPORT_COLUMNS idiom. `get` → string.
const EXPORT_COLUMNS = [
  { header: "Category", get: (row) => row.category || "" },
  { header: "Subcategory", get: (row) => formatSubcategory(row.subcategory) },
  ...ACCOMMODATIONS.map((acc) => ({
    header: acc.label,
    get: (row) => formatAffinity(row[acc.field]),
  })),
  { header: "Tier", get: (row) => (row.tier != null ? String(row.tier) : "") },
  { header: "Tier Label", get: (row) => TIER_CONFIG[Number(row.tier)]?.label || "" },
  { header: "Notes", get: (row) => row.notes || "" },
];

const EXPORT_HEADERS = EXPORT_COLUMNS.map((column) => column.header);
const toExportRow = (row) => EXPORT_COLUMNS.map((column) => column.get(row));

const TierBadge = ({ tier }) => {
  const cfg = TIER_CONFIG[tier];
  if (!cfg) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cfg.pill}`}>
      Tier {tier} · {cfg.label}
    </span>
  );
};

const ColumnHeader = ({ label, accommodationKey }) => {
  const cfg = ACCOMMODATION_COLORS[accommodationKey];
  return (
    <th className="px-3 py-2 text-center w-24">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg?.dot || "#9ca3af" }} />
        {label}
      </div>
    </th>
  );
};

const Legend = () => (
  <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400">
    <span className="inline-flex items-center gap-2">
      <span className="uppercase tracking-wider font-semibold">Affinity</span>
      <span className="inline-flex items-center">
        <span className="inline-block w-24 h-3 rounded border border-gray-200 dark:border-gray-700" style={{ background: "linear-gradient(to right, rgba(16,185,129,0.02), rgba(16,185,129,1))" }} />
      </span>
      <span>0.0 → 1.0</span>
    </span>
    <span className="inline-flex items-center gap-3">
      {Object.entries(TIER_CONFIG).map(([tier, cfg]) => (
        <span key={tier} className="inline-flex items-center gap-1">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${cfg.pill.split(" ")[0]}`} />
          <span>T{tier} {cfg.label}</span>
        </span>
      ))}
    </span>
  </div>
);

const CategoryAffinityPanel = ({ loading, error, rows }) => {
  const [tierFilter, setTierFilter] = useState("all");
  const [hideZeros, setHideZeros] = useState(false);

  const processed = useMemo(() => {
    const categoryRows = Array.isArray(rows) ? [...rows] : [];
    const filtered = categoryRows.filter((row) => {
      if (tierFilter !== "all" && Number(row.tier) !== Number(tierFilter)) return false;
      if (hideZeros) {
        const maxAffinity = Math.max(
          Number(row.rv_affinity || 0),
          Number(row.cabin_affinity || 0),
          Number(row.glamping_affinity || 0),
          Number(row.tent_affinity || 0)
        );
        if (maxAffinity < 0.1) return false;
      }
      return true;
    });
    filtered.sort((rowA, rowB) => {
      const tierA = Number(rowA.tier || 99);
      const tierB = Number(rowB.tier || 99);
      if (tierA !== tierB) return tierA - tierB;
      const maxA = Math.max(rowA.rv_affinity || 0, rowA.cabin_affinity || 0, rowA.glamping_affinity || 0, rowA.tent_affinity || 0);
      const maxB = Math.max(rowB.rv_affinity || 0, rowB.cabin_affinity || 0, rowB.glamping_affinity || 0, rowB.tent_affinity || 0);
      if (maxA !== maxB) return maxB - maxA;
      return String(rowA.category || "").localeCompare(String(rowB.category || ""));
    });
    return filtered;
  }, [rows, tierFilter, hideZeros]);

  // Export the filtered + sorted set the user is currently looking at (honors the
  // tier filter and hide-zeros toggle), matching OpportunityBoardPanel's behavior.
  const handleExportCsv = () => {
    downloadCsv(EXPORT_HEADERS, processed.map(toExportRow), "category-affinity.csv");
  };
  const handleExportPdf = () => {
    downloadPdf("Category Affinity", EXPORT_HEADERS, processed.map(toExportRow), "category-affinity.pdf");
  };

  return (
    <div className="p-5 border-t border-gray-200 dark:border-gray-800">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Category Affinity Heatmap</h3>
            <InfoTip text="A reference table of which event categories tend to pair well with which accommodation types — built from historical patterns, not from your data. The dashboard uses it to match events against your inventory." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Global reference: how strongly each event category pairs with each accommodation type.
            Darker cells = stronger fit.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {loading ? "Loading…" : `${processed.length} categor${processed.length === 1 ? "y" : "ies"}`}
          </div>
          {!loading && !error && processed.length > 0 && (
            <ExportMenu onCsv={handleExportCsv} onPdf={handleExportPdf} />
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mr-1">
            Tier
          </span>
          {[
            { key: "all", label: "All" },
            { key: "1", label: "Tier 1" },
            { key: "2", label: "Tier 2" },
            { key: "3", label: "Tier 3" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setTierFilter(opt.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                tierFilter === opt.key
                  ? "bg-[#32B1FF] text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={hideZeros}
            onChange={(event) => setHideZeros(event.target.checked)}
            className="accent-[#32B1FF]"
          />
          Hide low-affinity rows (&lt; 0.10)
        </label>
      </div>

      {loading ? (
        <SkeletonText lines={8} lineClass="h-10" gapClass="space-y-1" />
      ) : error ? (
        <ErrorState message={error} />
      ) : processed.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No categories match the current filter.
        </div>
      ) : (
        <>
          <ThinScroll className="overflow-x-auto -mx-1 responsive-card-table-wrapper">
            <table className="w-full text-sm table-fixed min-w-[900px] responsive-card-table">
              <colgroup>
                <col style={{ width: "auto" }} />
                <col style={{ width: "96px" }} />
                <col style={{ width: "96px" }} />
                <col style={{ width: "96px" }} />
                <col style={{ width: "96px" }} />
                <col style={{ width: "200px" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                    Category
                  </th>
                  {ACCOMMODATIONS.map((acc) => (
                    <ColumnHeader key={acc.key} label={acc.label} accommodationKey={acc.key} />
                  ))}
                  <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                    Tier
                  </th>
                </tr>
              </thead>
              <tbody>
                {processed.map((row, index) => {
                  const sub = formatSubcategory(row.subcategory);
                  const key = `${row.category}-${sub}-${index}`;
                  return (
                    <tr key={key} className="border-b border-gray-100 dark:border-gray-800/70">
                      <td className="px-3 py-2.5 align-top max-[499px]:!flex-col max-[499px]:!items-stretch max-[499px]:!gap-1" data-label="Category">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 max-[499px]:!text-left">
                          {row.category}
                          {sub && (
                            <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">
                              / {sub}
                            </span>
                          )}
                        </div>
                        {row.notes && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug max-[499px]:!text-left">
                            {row.notes}
                          </div>
                        )}
                      </td>
                      {ACCOMMODATIONS.map((acc) => {
                        const affinity = Number(row[acc.field] || 0);
                        return (
                          <td key={acc.key} className="p-1" data-label={acc.label}>
                            <div
                              className={`w-full max-[499px]:w-[60%] h-11 max-[499px]:h-7 rounded-md flex items-center justify-center text-sm max-[499px]:text-xs border border-gray-200 dark:border-gray-800 ${affinityTextClass(affinity)}`}
                              style={affinityCellStyle(affinity)}
                              title={`${acc.label}: ${formatAffinity(affinity)}`}
                            >
                              {formatAffinity(affinity)}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2.5 align-top whitespace-nowrap" data-label="Tier">
                        <TierBadge tier={Number(row.tier)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ThinScroll>

          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
            <Legend />
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryAffinityPanel;
